import React, { useState, useEffect } from 'react';
import { db, auth } from './firebase';
import { doc, updateDoc, setDoc, getDoc } from 'firebase/firestore';

const TagManager = ({ record, onTagsUpdate }) => {
  const [availableTags, setAvailableTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState(record.tags || []);
  const [newTag, setNewTag] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadUserTags();
  }, []);

  const loadUserTags = async () => {
    if (!auth.currentUser) return;
    
    try {
      const userRef = doc(db, 'users', auth.currentUser.uid);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists() && userDoc.data().tags) {
        setAvailableTags(userDoc.data().tags);
      }
    } catch (error) {
      console.error('載入標籤失敗:', error);
    }
  };

  const saveUserTags = async (tags) => {
    if (!auth.currentUser) return;
    
    try {
      const userRef = doc(db, 'users', auth.currentUser.uid);
      await updateDoc(userRef, { tags });
    } catch (error) {
      // 如果文件不存在，創建新文件
      const userRef = doc(db, 'users', auth.currentUser.uid);
      await setDoc(userRef, { tags }, { merge: true });
    }
  };

  const handleAddTag = async () => {
    if (!newTag.trim()) return;
    
    const tagName = newTag.trim();
    
    // 添加到可用標籤列表
    const updatedAvailableTags = [...new Set([...availableTags, tagName])];
    setAvailableTags(updatedAvailableTags);
    await saveUserTags(updatedAvailableTags);
    
    // 添加到選中標籤
    const updatedSelectedTags = [...new Set([...selectedTags, tagName])];
    setSelectedTags(updatedSelectedTags);
    
    setNewTag('');
  };

  const handleToggleTag = (tag) => {
    const updatedTags = selectedTags.includes(tag)
      ? selectedTags.filter(t => t !== tag)
      : [...selectedTags, tag];
    
    setSelectedTags(updatedTags);
  };

  const handleSaveTags = async () => {
    setLoading(true);
    
    try {
      // 更新記錄的標籤
      const recordRef = doc(db, 'businessCardsnotenew', record.id);
      await updateDoc(recordRef, { tags: selectedTags });
      
      onTagsUpdate(selectedTags);
    } catch (error) {
      console.error('更新標籤失敗:', error);
      alert('更新標籤失敗，請重試');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="tag-manager">
      <h4>標籤管理</h4>
      
      {/* 新增標籤 */}
      <div className="add-tag-section">
        <input
          type="text"
          value={newTag}
          onChange={(e) => setNewTag(e.target.value)}
          placeholder="新增標籤..."
          className="tag-input"
          onKeyPress={(e) => e.key === 'Enter' && handleAddTag()}
        />
        <button onClick={handleAddTag} className="add-tag-btn">
          +
        </button>
      </div>
      
      {/* 可用標籤 */}
      <div className="available-tags">
        {availableTags.map(tag => (
          <button
            key={tag}
            onClick={() => handleToggleTag(tag)}
            className={`tag-btn ${selectedTags.includes(tag) ? 'selected' : ''}`}
          >
            {tag}
          </button>
        ))}
      </div>
      
      {/* 已選標籤 */}
      {selectedTags.length > 0 && (
        <div className="selected-tags">
          <span>已選標籤: </span>
          {selectedTags.map(tag => (
            <span key={tag} className="selected-tag">
              {tag}
              <button 
                onClick={() => handleToggleTag(tag)}
                className="remove-tag-btn"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
      
      <button 
        onClick={handleSaveTags}
        disabled={loading}
        className="save-tags-btn"
      >
        {loading ? '儲存中...' : '儲存標籤'}
      </button>

      <style jsx="true">{`
        .tag-manager {
          background: rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.3);
          border-radius: 12px;
          padding: 16px;
          margin-top: 16px;
        }

        .tag-manager h4 {
          margin: 0 0 12px 0;
          color: rgba(108, 99, 143, 0.9);
          font-size: 14px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .add-tag-section {
          display: flex;
          gap: 8px;
          margin-bottom: 12px;
        }

        .tag-input {
          flex: 1;
          padding: 8px 12px;
          border: 1px solid rgba(255, 255, 255, 0.4);
          border-radius: 8px;
          background: rgba(255, 255, 255, 0.3);
          backdrop-filter: blur(10px);
          font-size: 14px;
          color: rgba(108, 99, 143, 0.9);
          outline: none;
        }

        .tag-input:focus {
          border-color: rgba(102, 126, 234, 0.6);
          box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.2);
        }

        .add-tag-btn {
          padding: 8px 12px;
          border: none;
          border-radius: 8px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          font-size: 16px;
          font-weight: bold;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .add-tag-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 8px rgba(102, 126, 234, 0.3);
        }

        .available-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          margin-bottom: 12px;
        }

        .tag-btn {
          padding: 6px 12px;
          border: 1px solid rgba(255, 255, 255, 0.4);
          border-radius: 16px;
          background: rgba(255, 255, 255, 0.3);
          color: rgba(108, 99, 143, 0.9);
          font-size: 12px;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .tag-btn:hover {
          background: rgba(255, 255, 255, 0.4);
          transform: translateY(-1px);
        }

        .tag-btn.selected {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border-color: rgba(102, 126, 234, 0.6);
          box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
        }

        .selected-tags {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 8px;
          margin-bottom: 12px;
          font-size: 12px;
          color: rgba(108, 99, 143, 0.8);
        }

        .selected-tag {
          display: flex;
          align-items: center;
          gap: 4px;
          padding: 4px 8px;
          background: rgba(102, 126, 234, 0.2);
          border-radius: 12px;
          font-size: 12px;
          color: rgba(108, 99, 143, 0.9);
        }

        .remove-tag-btn {
          border: none;
          background: none;
          color: rgba(108, 99, 143, 0.6);
          cursor: pointer;
          font-size: 14px;
          font-weight: bold;
          padding: 0;
          margin-left: 4px;
        }

        .remove-tag-btn:hover {
          color: rgba(108, 99, 143, 0.9);
        }

        .save-tags-btn {
          width: 100%;
          padding: 10px 16px;
          border: none;
          border-radius: 8px;
          background: linear-gradient(135deg, #56ab2f 0%, #a8e6cf 100%);
          color: white;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .save-tags-btn:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 4px 8px rgba(86, 171, 47, 0.3);
        }

        .save-tags-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        @media (max-width: 768px) {
          .tag-manager {
            padding: 12px;
          }
          
          .add-tag-section {
            flex-direction: column;
          }
          
          .add-tag-btn {
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default TagManager;